import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  StackIcon, UploadSimpleIcon, DownloadSimpleIcon, MagnifyingGlassIcon,
  UserPlusIcon, LightningIcon, CheckIcon, PrinterIcon,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const ST = {
  active: "border-primary/40 text-primary",
  printing: "border-accent/40 text-accent",
  pending_review: "border-accent/40 text-accent",
  unclaimed: "border-white/25 text-white/50",
  unallocated: "border-white/20 text-white/40",
  paused: "border-white/30 text-white/60",
  disabled: "border-destructive/40 text-destructive",
};

function Row({ tag, selected, onToggle, onSaved }) {
  const [url, setUrl] = useState(tag.destination_url || "");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { activate } when a guard is needed

  const existing = (tag.destination_url || "").trim();
  const changed = url.trim() !== existing;
  const isLive = tag.status === "active";
  const assignedTo = tag.pending_owner_email;

  const run = async (activate) => {
    setBusy(true);
    try {
      await api.put(`/admin/inventory/${tag.code}`, { destination_url: url.trim(), activate });
      toast.success(activate ? `/${tag.code} is live` : `/${tag.code} saved`);
      setConfirm(null);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  // Guard: if the code already has a destination, confirm before overwriting/activating.
  const request = (activate) => {
    if (existing && (changed || activate)) setConfirm({ activate });
    else run(activate);
  };

  const printFiles = async () => {
    try {
      const { data } = await api.get(`/admin/inventory/${tag.code}/print.pdf`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(data);
      link.download = `dcode_${tag.code}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(tag.retail ? "Print files downloaded (QR + insert)" : "QR file downloaded");
    } catch { toast.error("Could not generate print file"); }
  };

  return (
    <div data-testid={`inv-row-${tag.code}`} className="grid grid-cols-12 items-center gap-3 border-b border-border px-4 py-3 hover:bg-secondary/20 transition-colors">
      <div className="col-span-1 flex items-center">
        <Checkbox data-testid={`inv-select-${tag.code}`} checked={selected} onCheckedChange={() => onToggle(tag.code)} />
      </div>
      <div className="col-span-2 min-w-0">
        <p className="font-mono text-sm text-primary truncate">/{tag.code}</p>
        {assignedTo && <p className="font-mono text-[10px] text-white/40 truncate">{assignedTo}</p>}
      </div>
      <div className="col-span-2">
        <Badge variant="outline" className={`font-mono text-[10px] uppercase ${ST[tag.status] || "border-white/20 text-white/40"}`} data-testid={`inv-status-${tag.code}`}>{tag.status}</Badge>
        {tag.retail && <Badge variant="outline" data-testid={`inv-retail-${tag.code}`} className="ml-1.5 font-mono text-[10px] uppercase border-primary/40 text-primary">retail</Badge>}
      </div>
      <div className="col-span-4">
        <Input data-testid={`inv-url-${tag.code}`} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://destination.com" className="h-9 bg-secondary/40 border-border font-mono text-xs focus-visible:ring-primary" />
      </div>
      <div className="col-span-3 flex items-center justify-end gap-2">
        <Button data-testid={`inv-print-${tag.code}`} onClick={printFiles} disabled={busy} variant="outline" size="sm" title={tag.retail ? "Print files — QR + activation insert (PIN)" : "Print the QR"} className="border-border bg-transparent hover:bg-secondary hover:text-primary font-mono text-[11px] px-2">
          <PrinterIcon weight="bold" className="mr-1" />{tag.retail ? "PRINT" : "QR"}
        </Button>
        <Button data-testid={`inv-save-${tag.code}`} onClick={() => request(false)} disabled={busy} variant="outline" size="sm" className="border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-[11px]">SAVE</Button>
        <Button data-testid={`inv-activate-${tag.code}`} onClick={() => request(true)} disabled={busy} size="sm" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 font-mono text-[11px]">ACTIVATE</Button>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid={`inv-confirm-${tag.code}`} className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Overwrite the destination for /{tag.code}?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 space-y-3">
              {(isLive || assignedTo) && (
                <span className="block text-accent font-mono text-xs">
                  ⚠ This code is {isLive ? "LIVE" : "assigned"}{assignedTo ? ` to ${assignedTo}` : ""}. Changing it takes effect immediately.
                </span>
              )}
              <span className="block">Current destination:</span>
              <span className="block font-mono text-xs text-white/70 break-all border border-border bg-secondary/30 p-2">{existing || "—"}</span>
              <span className="block">Will change to:</span>
              <span className="block font-mono text-xs text-primary break-all border border-border bg-secondary/30 p-2">{url.trim() || "— (empty) —"}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`inv-confirm-cancel-${tag.code}`} className="border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-xs">CANCEL</AlertDialogCancel>
            <AlertDialogAction data-testid={`inv-confirm-ok-${tag.code}`} onClick={(e) => { e.preventDefault(); run(confirm?.activate); }} disabled={busy} className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">{busy ? "SAVING…" : confirm?.activate ? "OVERWRITE & ACTIVATE" : "OVERWRITE"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Inventory() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  const [assignEmail, setAssignEmail] = useState("");
  const [assignActivate, setAssignActivate] = useState(false);
  const [csvActivate, setCsvActivate] = useState(true);
  const [csvResult, setCsvResult] = useState(null);
  const [genQty, setGenQty] = useState("");
  const [genLabel, setGenLabel] = useState("");
  const [genRetail, setGenRetail] = useState(false);
  const [retailOnly, setRetailOnly] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      if (retailOnly) params.retail = true;
      const { data } = await api.get("/admin/inventory", { params });
      setItems(data.items); setTotal(data.total);
    } catch { setItems([]); }
  }, [status, q, retailOnly]);

  const loadSummary = useCallback(async () => {
    try { const { data } = await api.get("/admin/inventory/summary"); setSummary(data); } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const refresh = () => { load(); loadSummary(); };

  const selectedCodes = Object.keys(selected).filter((c) => selected[c]);
  const toggle = (code) => setSelected((s) => ({ ...s, [code]: !s[code] }));
  const clearSel = () => setSelected({});

  const assign = async () => {
    if (!assignEmail.trim()) return toast.error("Enter a customer email");
    if (selectedCodes.length === 0) return toast.error("Select at least one code");
    try {
      const { data } = await api.post("/admin/inventory/assign", {
        codes: selectedCodes, email: assignEmail.trim(), activate: assignActivate,
      });
      const skipped = (data.already_assigned || []).length + (data.missing || []).length;
      toast.success(`Assigned ${data.assigned.length} code(s) to ${data.email}`, {
        description: skipped ? `${skipped} skipped (already assigned or unknown)` : undefined,
      });
      setAssignEmail(""); clearSel(); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign"); }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/admin/inventory/template.csv", { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(data);
      link.download = "dcode_inventory_template.csv";
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Template downloaded");
    } catch { toast.error("Could not download template"); }
  };

  const generate = async () => {
    const qty = parseInt(genQty, 10);
    if (!qty || qty < 1) return toast.error("Enter how many codes to create");
    setGenBusy(true);
    try {
      const { data } = await api.post("/admin/inventory/generate", { quantity: qty, label: genLabel.trim() || null, retail: genRetail });
      toast.success(`Generated ${data.quantity} ${genRetail ? "retail " : ""}codes`, { description: data.label });
      setGenQty(""); setGenLabel(""); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate"); }
    finally { setGenBusy(false); }
  };

  const downloadInventory = async () => {
    try {
      const params = {};
      if (selectedCodes.length > 0) params.codes = selectedCodes.join(",");
      else {
        if (status !== "all") params.status = status;
        if (q.trim()) params.q = q.trim();
      }
      const { data } = await api.get("/admin/inventory/export.csv", { params, responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(data);
      link.download = "dcode_inventory.csv";
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(selectedCodes.length ? `Downloaded ${selectedCodes.length} selected` : "Downloaded current view");
    } catch { toast.error("Could not download"); }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("activate", csvActivate ? "true" : "false");
    try {
      const { data } = await api.post("/admin/inventory/csv", form);
      setCsvResult(data);
      toast.success(`Applied ${data.applied} code(s)${data.skipped.length ? `, ${data.skipped.length} skipped` : ""}`);
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const counts = summary?.status_counts || {};

  return (
    <Layout>
      <div className="mb-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-3">// Admin · inventory</p>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">Code inventory</h1>
        <p className="text-white/50 mt-4 max-w-2xl">Set destinations on pre-made codes, mark them claimed, and assign pins to a customer — one by one, in bulk, or via CSV. The Shopify auto-allocation still runs untouched.</p>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 border border-border mb-8" data-testid="inv-summary">
        <div className="p-5 border-r border-border">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Total codes</p>
          <p className="font-mono text-2xl mt-2">{summary ? summary.total : "—"}</p>
        </div>
        <div className="p-5 border-r border-border">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Unallocated</p>
          <p className="font-mono text-2xl mt-2 text-white/60">{counts.unallocated || 0}</p>
        </div>
        <div className="p-5 border-r border-border">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Pending</p>
          <p className="font-mono text-2xl mt-2 text-accent">{(counts.pending_review || 0) + (counts.unclaimed || 0)}</p>
        </div>
        <div className="p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Live</p>
          <p className="font-mono text-2xl mt-2 text-primary">{counts.active || 0}</p>
        </div>
      </div>

      {/* Generate codes */}
      <div className="border border-border bg-card p-5 mb-6">
        <div className="flex items-center gap-2 text-primary mb-4"><LightningIcon weight="duotone" size={18} /><span className="font-mono text-[11px] uppercase tracking-[0.2em]">Generate new codes</span></div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">How many</label>
            <Input data-testid="inv-gen-qty" type="number" min="1" max="5000" value={genQty} onChange={(e) => setGenQty(e.target.value)} placeholder="e.g. 100" className="mt-1 w-32 bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Batch label (optional)</label>
            <Input data-testid="inv-gen-label" value={genLabel} onChange={(e) => setGenLabel(e.target.value)} placeholder="e.g. Enamel Pins — Oct run" className="mt-1 bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary" />
          </div>
          <Button data-testid="inv-gen-btn" onClick={generate} disabled={genBusy} className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 font-mono text-xs h-10">
            <LightningIcon weight="bold" className="mr-1.5" /> {genBusy ? "GENERATING…" : "GENERATE CODES"}
          </Button>
        </div>
        <label className="flex items-center gap-2 font-mono text-[11px] text-white/60 mt-4">
          <Switch data-testid="inv-gen-retail" checked={genRetail} onCheckedChange={setGenRetail} />
          Retail / in-store stock — mint a scratch-off PIN with each code
        </label>
        <p className="font-mono text-[10px] text-white/40 mt-3">
          {genRetail
            ? <>Creates <span className="text-primary">retail</span> codes. Each gets a hidden PIN (in the CSV export) for buyers to self-claim after purchase. Scanning an unclaimed one shows a branded activation page.</>
            : <>Creates blank <span className="text-white/70">unallocated</span> codes ready to assign or set destinations on. Up to 5000 at a time.</>}
        </p>
      </div>


      {/* CSV panel */}
      <div className="border border-border bg-card p-5 mb-6">
        <div className="flex items-center gap-2 text-accent mb-4"><StackIcon weight="duotone" size={18} /><span className="font-mono text-[11px] uppercase tracking-[0.2em]">CSV bulk edit</span></div>
        <div className="flex flex-wrap items-center gap-3">
          <Button data-testid="inv-template-btn" onClick={downloadTemplate} variant="outline" className="border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-xs">
            <DownloadSimpleIcon weight="bold" className="mr-1.5" /> DOWNLOAD TEMPLATE
          </Button>
          <input ref={fileRef} type="file" accept=".csv" onChange={uploadCsv} className="hidden" data-testid="inv-csv-input" />
          <Button data-testid="inv-upload-btn" onClick={() => fileRef.current?.click()} className="bg-accent text-accent-foreground font-bold hover:bg-accent/90 font-mono text-xs">
            <UploadSimpleIcon weight="bold" className="mr-1.5" /> UPLOAD CSV
          </Button>
          <label className="flex items-center gap-2 font-mono text-[11px] text-white/60 ml-1">
            <Switch data-testid="inv-csv-activate" checked={csvActivate} onCheckedChange={setCsvActivate} />
            Mark uploaded codes live
          </label>
        </div>
        <p className="font-mono text-[10px] text-white/40 mt-3">Columns: <span className="text-white/70">code, destination_url, email</span> (email optional). Rows with bad URLs or unknown codes are skipped and reported.</p>
        {csvResult && (
          <div data-testid="inv-csv-result" className="mt-4 border border-border p-3 font-mono text-[11px]">
            <p className="text-primary">Applied {csvResult.applied} code(s).</p>
            {csvResult.skipped.length > 0 && (
              <div className="mt-2 text-white/50">
                <p className="text-accent">Skipped {csvResult.skipped.length}:</p>
                {csvResult.skipped.slice(0, 10).map((s, i) => (
                  <p key={i}>row {s.row} · {s.code || "—"} · {s.reason}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Assign panel */}
      <div className="border border-border bg-card p-5 mb-6">
        <div className="flex items-center gap-2 text-primary mb-4"><UserPlusIcon weight="duotone" size={18} /><span className="font-mono text-[11px] uppercase tracking-[0.2em]">Assign selected to a customer</span></div>
        <div className="flex flex-wrap items-center gap-3">
          <span data-testid="inv-selected-count" className="font-mono text-xs text-white/60 border border-border px-3 py-2">{selectedCodes.length} selected</span>
          <Input data-testid="inv-assign-email" value={assignEmail} onChange={(e) => setAssignEmail(e.target.value)} placeholder="customer@email.com" className="max-w-xs bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary" />
          <label className="flex items-center gap-2 font-mono text-[11px] text-white/60">
            <Switch data-testid="inv-assign-activate" checked={assignActivate} onCheckedChange={setAssignActivate} />
            Activate now
          </label>
          <Button data-testid="inv-assign-btn" onClick={assign} className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 font-mono text-xs">
            <CheckIcon weight="bold" className="mr-1.5" /> ASSIGN
          </Button>
          {selectedCodes.length > 0 && <button onClick={clearSel} className="font-mono text-[11px] text-white/40 hover:text-primary">clear</button>}
        </div>
        <p className="font-mono text-[10px] text-white/40 mt-3">Binds the codes to that email and makes the customer eligible — they appear in their portal to confirm{assignActivate ? " (going live immediately)" : " (pending until they confirm)"}.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, email or URL" className="pl-9 bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="inv-status-filter" className="w-44 bg-secondary/40 border-border font-mono text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="unallocated">Unallocated</SelectItem>
            <SelectItem value="pending_review">Pending review</SelectItem>
            <SelectItem value="unclaimed">Unclaimed</SelectItem>
            <SelectItem value="active">Active (live)</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
        <span className="font-mono text-[11px] text-white/40">{total} result{total === 1 ? "" : "s"}</span>
        <label className="flex items-center gap-2 font-mono text-[11px] text-white/60">
          <Switch data-testid="inv-retail-filter" checked={retailOnly} onCheckedChange={setRetailOnly} />
          Retail only
        </label>
        <Button data-testid="inv-download-btn" onClick={downloadInventory} variant="outline" className="border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-xs">
          <DownloadSimpleIcon weight="bold" className="mr-1.5" /> {selectedCodes.length > 0 ? `DOWNLOAD ${selectedCodes.length} SELECTED` : "DOWNLOAD CSV"}
        </Button>
      </div>

      {/* Table */}
      <div className="border border-border" data-testid="inv-table">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-border bg-secondary/20 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
          <div className="col-span-1">Sel</div>
          <div className="col-span-2">Code</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-4">Destination</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {items === null ? (
          <p className="font-mono text-sm text-primary animate-pulse px-4 py-6">LOADING…</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center text-center py-16 px-6">
            <LightningIcon size={38} weight="duotone" className="text-white/30" />
            <p className="font-heading font-bold text-lg mt-4">No codes match</p>
            <p className="text-white/40 text-sm mt-1">Generate a batch in the fulfilment console, then set destinations here.</p>
          </div>
        ) : (
          items.map((t) => <Row key={t.code} tag={t} selected={!!selected[t.code]} onToggle={toggle} onSaved={refresh} />)
        )}
      </div>
    </Layout>
  );
}
